import { isAntonioError } from '@ackee/antonio-core';
import { put, takeEvery } from 'redux-saga/effects';

import config from '@/config';
import { logger } from '@/config/logger';
import { api } from '@/modules/api/fetcher';
import { actions as messagesActions } from '@/modules/messages';
import { actions as modalsActions, StaticModal } from '@/modules/modals';
import { ErrorCode, isApiAntonioError, isSaasLimitReachedError, isTeamVaultLimitReachedError } from '@/services/utilities/isApiError';

import { Fields } from '../../constants';
import type { ImportValidationErrorData } from '../../types';
import { isNonIgnorableImportError } from '../../utilities/isNonIgnorableImportError';
import { importValidationRequest, importValidationRequestFailure, importValidationRequestSuccess } from '../actions';

function* importValidationHandler({ payload }: ReturnType<typeof importValidationRequest>) {
    try {
        // TODO: use it once API spec is fixed
        // yield validateSecrets({
        //     [Fields.SECRETS]: payload.secrets,
        //     [Fields.SECRETS_FORMAT]: payload.format,
        //     [Fields.DELIMITER]: payload.delimiter,
        // });

        const formData = new FormData();
        formData.append(Fields.SECRETS, payload.secrets);
        formData.append(Fields.SECRETS_FORMAT, payload.format);
        formData.append(Fields.DELIMITER, payload.delimiter);

        if (payload.vaultId) {
            yield* api().post(config.api.vaultImportValidation, formData, { uriParams: { vaultId: payload.vaultId } });
        } else {
            yield* api().post(config.api.importValidation, formData);
        }

        yield put(importValidationRequestSuccess());
    } catch (e) {
        if (isAntonioError(e)) {
            const statusCode = Number(e.response?.status) || 500;

            if (statusCode >= 500) {
                logger.error(e);

                yield put(
                    messagesActions.displayErrorMessage({
                        message: { id: 'error.import-export.invalidImportFile' },
                    }),
                );
                yield put(importValidationRequestFailure());

                return;
            }

            if (isSaasLimitReachedError(e)) {
                yield put(importValidationRequestFailure());

                yield put(modalsActions.setModalVisibility(StaticModal.IMPORT_LIMIT_REACHED, true, e.data.errorData));

                return;
            }

            if (isNonIgnorableImportError(e)) {
                yield put(importValidationRequestFailure(true));

                return;
            }

            // The remaining row problems are ignorable: ask the user whether to continue.
            if (isApiAntonioError<ImportValidationErrorData>(e) && e.data.errorCode === ErrorCode.IMPORT_VALIDATION_INVALID_SECRETS) {
                yield put(modalsActions.setModalVisibility(StaticModal.IMPORT_VALIDATION, true, e.data.errorData));

                return;
            }
        }

        logger.error(e);

        yield put(
            messagesActions.displayErrorMessage({
                message: { id: isTeamVaultLimitReachedError(e) ? 'vaults.form.error.limitReached' : 'error.import-export.import' },
            }),
        );
        yield put(importValidationRequestFailure());
    }
}

export default function* importValidationSaga() {
    yield takeEvery(importValidationRequest.toString(), importValidationHandler);
}
